// src/components/ProtectedRoute/Admin/Consoles/Framework/ConsoleTable.jsx
import * as React from "react";
import { Box } from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";

export default function ConsoleTable({
  columns,
  rows,
  total,
  page,
  onPageChange,
  pageSize,
  onPageSizeChange,
  sortModel,
  onSortModelChange,
  onRowClick,      // 任意: ダブルクリックで編集など
  selectionModel,
  onSelectionModelChange,
  checkboxSelection = false,
  loading,
  height = 640,
  sx = {},         // DataGrid への追加スタイル
}) {
  // 行高を可変にし、セル内の複数行テキスト（著者リストなど）を表示できるようにする
  const mergedSx = {
    "& .MuiDataGrid-cell": {
      alignItems: "flex-start",
      whiteSpace: "normal",
      lineHeight: 1.4,
      py: 1,
    },
    "& .col-actions": { whiteSpace: "nowrap", py: 0.5 },
    ...sx,
  };

  return (
    <Box sx={{ height, width: "100%" }}>
      <DataGrid
        rows={rows}
        columns={columns}
        getRowId={(r) => r.id} // publications は id 文字列（CompositeでもOK）
        rowCount={total}

        // サーバーモード
        paginationMode="server"
        sortingMode="server"

        // ページング
        paginationModel={{ page, pageSize }}
        onPaginationModelChange={(m) => {
          if (m.page !== page) onPageChange?.(m.page);
          if (m.pageSize !== pageSize) onPageSizeChange?.(m.pageSize);
        }}

        // ソート
        sortModel={sortModel}
        onSortModelChange={onSortModelChange}

        // UX
        disableRowSelectionOnClick
        checkboxSelection={checkboxSelection}
        selectionModel={selectionModel}
        onSelectionModelChange={onSelectionModelChange}
        onRowDoubleClick={(p) => onRowClick?.(p.row)}
        loading={loading}

        // ★ ここがポイント：ポップアップ類はポータル無効（Dialog配下に描画）
        slotProps={{
          basePopper: { disablePortal: true },
        }}

        // 複数行テキストのために行高を自動化
        getRowHeight={() => "auto"}
        // 選択モデルの互換性: 親が渡す selectionModel の形に合わせて
        // DataGrid に渡す `model` prop を切り替えつつ、発火される
        // どちらのコールバック（旧: onSelectionModelChange /
        // 新: onRowSelectionModelChange）が来ても捕まえて
        // 親に正規化して伝播する。
        {...(Array.isArray(selectionModel)
          ? {
              // 親は配列を使っている（旧スタイル）。Model prop は配列で渡す。
              selectionModel: selectionModel,
              // 古いコールバックが呼ばれた場合（通常は配列を受け取る）
              onSelectionModelChange: (m) => {
                const nextArray = Array.isArray(m) ? m : m?.selectionModel ?? [];
                onSelectionModelChange?.(nextArray);
              },
              // 新しいコールバックが呼ばれた場合も捕まえる（DataGrid の実装差を吸収）
              onRowSelectionModelChange: (m) => {
                // m が配列の場合とオブジェクトの場合を扱う
                if (Array.isArray(m)) {
                  onSelectionModelChange?.(m);
                } else if (m && typeof m === "object") {
                  // try to extract ids -> array
                  const ids = Array.from(m.ids || []);
                  onSelectionModelChange?.(ids);
                } else {
                  onSelectionModelChange?.([]);
                }
              },
            }
          : {
              // 親はオブジェクトスタイル（新API）を使っている。
              rowSelectionModel: selectionModel,
              onRowSelectionModelChange: (m) => {
                const model = m && typeof m === "object" ? m : { ids: new Set(Array.isArray(m) ? m : []) };
                onSelectionModelChange?.(model);
              },
              // 古いコールバックが来た場合も捕まえてオブジェクトに変換して渡す
              onSelectionModelChange: (m) => {
                if (Array.isArray(m)) {
                  onSelectionModelChange?.({ type: "include", ids: new Set(m) });
                } else if (m && typeof m === "object") {
                  onSelectionModelChange?.(m);
                } else {
                  onSelectionModelChange?.({ type: "include", ids: new Set() });
                }
              },
            })}
        sx={mergedSx}
      />
    </Box>
  );
}
